import React, { useState, useContext } from 'react'
import {
	Button, Modal, ModalHeader, ModalBody, ModalFooter,
	FormGroup, Label, Input, Alert
} from 'reactstrap'
import { SessionContext } from '../../contexts/SessionProvider'
import AuctionTabsService from '../../services/AuctionTabsService'

/**
 * Modal for ungrounding a vehicle (dealer requests vehicle back).
 * BRD: Dealer unground request workflow.
 */
const UngroundModal = ({ isOpen, toggle, auction, onSuccess }) => {
	const { sessionKey } = useContext(SessionContext)
	const [reason, setReason] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [error, setError] = useState(null)

	const handleSubmit = async () => {
		if (!reason.trim()) {
			setError('Please provide a reason for ungrounding this vehicle.')
			return
		}

		setIsSubmitting(true)
		setError(null)

		try {
			await AuctionTabsService.ungroundVehicle(auction?.auctionId, reason, sessionKey)
			setReason('')
			onSuccess?.()
			toggle()
		} catch (err) {
			setError(err.response?.data?.message || 'Failed to unground vehicle.')
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleClose = () => {
		setReason('')
		setError(null)
		toggle()
	}

	if (!auction) return null

	return (
		<Modal isOpen={isOpen} toggle={handleClose} centered>
			<ModalHeader toggle={handleClose}>
				Unground Vehicle
			</ModalHeader>
			<ModalBody>
				{error && <Alert color="danger">{error}</Alert>}
				
				<p>
					You are about to unground this vehicle and return it to the dealer:
				</p>
				<div className="bg-light p-3 rounded mb-3">
					<strong>{auction.year} {auction.make} {auction.model}</strong>
					<br />
					<small className="text-muted">VIN: {auction.vin}</small>
					{auction.stockNumber && (
						<>
							<br />
							<small className="text-muted">Stock #: {auction.stockNumber}</small>
						</>
					)}
					{auction.dealerName && (
						<>
							<br />
							<small className="text-muted">Dealer: {auction.dealerName}</small>
						</>
					)}
				</div>
				
				<FormGroup>
					<Label for="ungroundReason">Reason for Ungrounding <span className="text-danger">*</span></Label>
					<Input
						type="textarea"
						id="ungroundReason"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						placeholder="Enter the reason for ungrounding (required)..."
						rows={3}
						required
					/>
				</FormGroup>
				
				<Alert color="warning" className="mb-0">
					<small>
						<strong>Warning:</strong> Ungrounding this vehicle will remove it from the 
						auction process and return it to the dealer. This action cannot be undone 
						without re-listing the vehicle.
					</small>
				</Alert>
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" onClick={handleClose} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button color="danger" onClick={handleSubmit} disabled={isSubmitting || !reason.trim()}>
					{isSubmitting ? 'Processing...' : 'Unground Vehicle'}
				</Button>
			</ModalFooter>
		</Modal>
	)
}

export default UngroundModal
